"use client";

import clsx from "clsx";
import type { ChatEntitySearchResultsEmbed } from "./types";
import { ChatEntityCardRow } from "./ChatEntityCardRow";
import { EntityPreviewStatus } from "./EntityPreviewStatus";
import { useEntityPreviewNavigation } from "./useEntityPreviewNavigation";
import { CHAT_TOOL_STEP_DETAIL_MUTED_CLASS } from "./chatToolStepDetailUi";

function labelContainsHint(label: string, hint: string): boolean {
  const labelLower = label.toLowerCase();
  return hint
    .split("·")
    .map((part) => part.trim().toLowerCase())
    .filter(Boolean)
    .every((part) => labelLower.includes(part));
}

type SearchEmbedCandidateListProps = {
  embeds: ChatEntitySearchResultsEmbed[];
  /** Compact rows for expandable tool-step detail panels. */
  density?: "default" | "toolDetail";
};

export function SearchEmbedCandidateList({ embeds, density = "default" }: SearchEmbedCandidateListProps) {
  const { focusInList } = useEntityPreviewNavigation();
  const compact = density === "toolDetail";

  if (compact) {
    return (
      <div data-testid="chat-entity-search-candidate-list">
        <ul className="space-y-0">
          {embeds.flatMap((embed) =>
            embed.candidates.map((candidate) => (
              <li key={`${embed.entity_type}-${candidate.id}`}>
                <button
                  type="button"
                  data-testid="chat-entity-search-candidate"
                  className="flex w-full items-baseline gap-2 rounded px-1 py-0.5 text-left text-[11px] leading-snug text-slate-600 transition-colors hover:bg-slate-200/50 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-100"
                  onClick={() => focusInList(embed.entity_type, candidate.id, candidate.label)}
                >
                  <span className="min-w-0 flex-1 truncate font-normal text-slate-700 dark:text-slate-300">
                    {candidate.label}
                  </span>
                  {candidate.hint && !labelContainsHint(candidate.label, candidate.hint) ? (
                    <span className="shrink-0 truncate text-[10px] text-slate-500 dark:text-slate-500">
                      {candidate.hint}
                    </span>
                  ) : null}
                  {candidate.status_hint ? (
                    <span className="shrink-0 text-[10px] text-slate-500">{candidate.status_hint}</span>
                  ) : null}
                </button>
              </li>
            )),
          )}
        </ul>
        {embeds.some(
          (embed) => embed.truncated && embed.total_count > embed.candidates.length,
        ) ? (
          <p className={clsx("mt-1", CHAT_TOOL_STEP_DETAIL_MUTED_CLASS)}>
            {embeds
              .filter((embed) => embed.truncated && embed.total_count > embed.candidates.length)
              .map((embed) => `Showing ${embed.candidates.length} of ${embed.total_count} matches`)
              .join(" · ")}
          </p>
        ) : null}
      </div>
    );
  }

  return (
    <ul className="mt-1 space-y-0.5" data-testid="chat-entity-search-candidate-list">
      {embeds.flatMap((embed) =>
        embed.candidates.map((candidate) => (
          <li key={`${embed.entity_type}-${candidate.id}`}>
            <ChatEntityCardRow
              label={candidate.label}
              hint={
                candidate.hint && !labelContainsHint(candidate.label, candidate.hint)
                  ? candidate.hint
                  : undefined
              }
              testId="chat-entity-search-candidate"
              onClick={() => focusInList(embed.entity_type, candidate.id, candidate.label)}
              className="border-transparent bg-transparent px-0 py-1.5 text-xs shadow-none hover:border-slate-200/65 hover:bg-white/60 dark:hover:border-slate-600/50 dark:hover:bg-slate-900/25"
              trailing={
                candidate.status_hint ? (
                  <EntityPreviewStatus entityType={embed.entity_type} label={candidate.status_hint} />
                ) : null
              }
            />
          </li>
        )),
      )}
    </ul>
  );
}
